import { useEffect, useState } from "react";
import { Link } from "@tanstack/react-router";
import { ArrowUpRight, Gift, MessageSquareText, Star } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Textarea } from "@/components/ui/textarea";
import { useIsMobile } from "@/hooks/use-mobile";

const MIN_COMMENT_WORDS = 10;

function countWords(value: string) {
  return value.trim().split(/\s+/u).filter(Boolean).length;
}

/** Hàng sao chấm điểm câu trả lời AI (1..5). Bấm lại sao đang chọn để bỏ chấm. */
export function StarRating({
  value,
  busy,
  onRate,
  comment,
  commentRewarded,
  onComment,
  label = "Đánh giá:",
  className,
}: {
  value: number;
  busy?: boolean;
  onRate: (v: number | null) => void;
  comment?: string | null;
  commentRewarded?: boolean;
  onComment?: (comment: string | null) => void;
  label?: string;
  className?: string;
}) {
  const [hover, setHover] = useState(0);
  const [commentOpen, setCommentOpen] = useState(false);
  const [draft, setDraft] = useState(comment ?? "");
  const isMobile = useIsMobile();
  const shown = hover || value;
  const wordCount = countWords(draft);
  const canSubmitComment = wordCount >= MIN_COMMENT_WORDS;

  useEffect(() => {
    if (!commentOpen) setDraft(comment ?? "");
  }, [comment, commentOpen]);

  const saveComment = () => {
    if (!canSubmitComment) return;
    const next = draft.trim();
    onComment?.(next);
    setCommentOpen(false);
  };

  const commentTrigger = (
    <button
      type="button"
      disabled={busy}
      className={`inline-flex h-7 items-center gap-1 rounded-full px-2 text-[11px] transition focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:opacity-50 ${
        comment
          ? "bg-primary/12 text-primary"
          : "text-muted-foreground hover:bg-muted hover:text-foreground"
      }`}
      aria-label={comment ? "Sửa nhận xét" : "Thêm nhận xét"}
    >
      <MessageSquareText className="h-3.5 w-3.5" />
      <span>{comment ? "Đã nhận xét" : commentRewarded ? "Nhận xét" : "Nhận xét +1.000đ"}</span>
    </button>
  );

  const commentEditor = (
    <>
      <div
        className={`rounded-lg px-3 py-2 text-xs ${
          commentRewarded
            ? "bg-muted text-muted-foreground"
            : "bg-primary/10 font-medium text-primary"
        }`}
      >
        {commentRewarded
          ? "Bạn đã nhận 1.000đ cho câu trả lời này. Chỉnh sửa sẽ không nhận thêm thưởng."
          : "🎁 Gửi nhận xét đầu tiên để được hoàn 1.000đ vào ví Bói Đê."}
      </div>
      <Textarea
        value={draft}
        onChange={(event) => setDraft(event.target.value)}
        maxLength={500}
        rows={4}
        autoFocus
        placeholder="Điều gì hữu ích hoặc chưa chính xác?"
        className="min-h-24 resize-none text-sm"
      />
      <div className="flex items-center justify-between gap-3">
        <span
          className={`text-[11px] tabular-nums ${canSubmitComment ? "text-emerald-600" : "text-muted-foreground"}`}
        >
          {wordCount}/10 từ tối thiểu · {draft.length}/500 ký tự
        </span>
        <div className="flex shrink-0 gap-2">
          <Button type="button" size="sm" variant="ghost" onClick={() => setCommentOpen(false)}>
            Hủy
          </Button>
          <Button
            type="button"
            size="sm"
            disabled={busy || !canSubmitComment}
            onClick={saveComment}
          >
            Gửi
          </Button>
        </div>
      </div>
    </>
  );

  return (
    <div className={`flex flex-col gap-2 ${className ?? ""}`}>
      <div className="flex flex-wrap items-center gap-2">
        {label ? <span className="text-[11px] text-muted-foreground">{label}</span> : null}
        <div className="flex items-center gap-0.5" onMouseLeave={() => setHover(0)}>
          {[1, 2, 3, 4, 5].map((n) => (
            <button
              key={n}
              type="button"
              disabled={busy}
              onMouseEnter={() => setHover(n)}
              onClick={() => onRate(value === n ? null : n)}
              className="p-0.5 transition disabled:opacity-50"
              aria-label={`${n} sao`}
              aria-pressed={value === n}
            >
              <Star
                className={`h-4 w-4 transition ${
                  n <= shown
                    ? "fill-amber-400 text-amber-400"
                    : "text-muted-foreground/40 hover:text-amber-400/70"
                }`}
              />
            </button>
          ))}
        </div>
        {onComment ? (
          isMobile ? (
            <Dialog open={commentOpen} onOpenChange={setCommentOpen}>
              <DialogTrigger asChild>{commentTrigger}</DialogTrigger>
              <DialogContent
                hideClose
                className="max-h-[calc(100dvh-2rem)] !w-[calc(100vw-2rem)] !max-w-sm gap-3 overflow-y-auto rounded-2xl p-4"
              >
                <div>
                  <DialogTitle className="text-sm">Nhận xét câu trả lời</DialogTitle>
                  <DialogDescription className="mt-0.5 text-xs">
                    Viết tối thiểu 10 từ để Bói Đê hiểu rõ góp ý của bạn.
                  </DialogDescription>
                </div>
                {commentEditor}
              </DialogContent>
            </Dialog>
          ) : (
            <Popover open={commentOpen} onOpenChange={setCommentOpen}>
              <PopoverTrigger asChild>{commentTrigger}</PopoverTrigger>
              <PopoverContent
                align="end"
                sideOffset={8}
                collisionPadding={16}
                className="w-[min(22rem,calc(100vw-2rem))] space-y-3 p-3"
              >
                <div>
                  <p className="text-sm font-medium">Nhận xét câu trả lời</p>
                  <p className="mt-0.5 text-xs text-muted-foreground">
                    Viết tối thiểu 10 từ để Bói Đê hiểu rõ góp ý của bạn.
                  </p>
                </div>
                {commentEditor}
              </PopoverContent>
            </Popover>
          )
        ) : null}
      </div>
      <Link
        to="/cam-nhan"
        className="group inline-flex w-fit items-center gap-1.5 rounded-full border border-gold-soft bg-primary/10 px-3 py-1 text-[11px] font-medium text-primary transition hover:bg-primary/15 active:scale-95"
      >
        <Gift className="h-3.5 w-3.5" />
        Chia sẻ cảm nhận, nhận đến 1.000.000đ vào ví Bói
        <ArrowUpRight className="h-3 w-3 transition group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
      </Link>
    </div>
  );
}
